import { generateClient } from "aws-amplify/data";
import { getCurrentUser } from "aws-amplify/auth";
import { getCachedUserAttributes } from "./userAttributesCache";
import type { Schema } from "@backend-schema";

const getClient = () => generateClient<Schema>();

// Event name for subscription changes
export const SUBSCRIPTION_CHANGED_EVENT = "subscription_changed";

/**
 * Cancels a subscription
 */
export async function cancelSubscription(): Promise<void> {
  try {
    await getCurrentUser();

    // Dispatch subscription change event
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CHANGED_EVENT));
  } catch {
    throw new Error("Failed to cancel subscription");
  }
}

/**
 * Initiates a subscription
 */
export async function initiateSubscription(
  _planType: "monthly" | "yearly",
  _trial?: boolean,
): Promise<void> {
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CHANGED_EVENT));
}

/**
 * Handles checkout success
 */
export async function handleCheckoutSuccess(
  _paymentIntentId: string,
): Promise<void> {
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CHANGED_EVENT));
}

/**
 * Updates subscription tier
 */
export async function updateSubscriptionTier(
  _tier: "free" | "pro",
  _planType?: "monthly" | "yearly",
  _trial?: boolean,
  _subscriptionId?: string,
): Promise<void> {
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CHANGED_EVENT));
}

/**
 * Verifies if a company has an active Stripe subscription
 * This checks if the company domain has a valid subscription for framework access
 */
export async function verifyCompanySubscription(
  companyDomain: string,
): Promise<{
  hasSubscription: boolean;
  status?: "active" | "canceled" | "past_due" | "trialing";
  message?: string;
}> {
  try {
    // For template frameworks, always allow access
    if (companyDomain === "template.skillbubble.com") {
      return {
        hasSubscription: true,
        status: "active",
        message: "Template frameworks are available to all Pro users",
      };
    }

    // TODO: Implement company subscription verification
    // This would typically involve:
    // 1. Looking up company by domain in a CompanySubscription table
    // 2. Checking their Stripe subscription status
    // 3. Verifying the subscription is active

    // For now, we'll allow access to company frameworks if they exist
    // The framework creation itself should be restricted to company admins
    return {
      hasSubscription: true,
      status: "active",
      message: "Company framework access granted",
    };
  } catch (error) {
    console.error("Error verifying company subscription:", error);
    return {
      hasSubscription: false,
      message: "Unable to verify company subscription",
    };
  }
}

/**
 * Gets subscription details from Cognito user attributes (source of truth)
 */
export async function getSubscriptionDetails(): Promise<{
  tier: "free" | "pro";
  status: "active" | "canceled" | "past_due" | "trialing" | "none";
  currentPeriodEnd?: string;
  planType?: "monthly" | "yearly";
  trialEnd?: string;
}> {
  try {
    const attributes = await getCachedUserAttributes();
    const subscriptionStatus = attributes["custom:subscription_status"];
    const customerId = attributes["custom:stripe_customer_id"];

    if (!subscriptionStatus || subscriptionStatus === "none" || !customerId) {
      return { tier: "free", status: "none" };
    }

    // If we have an active/trialing status in Cognito, user is Pro
    if (subscriptionStatus === "active" || subscriptionStatus === "trialing") {
      // Try to get additional details from Stripe via GraphQL
      try {
        const user = await getCurrentUser();
        const profiles = await getClient().models.UserProfile.list();
        const userProfile = profiles.data.find(
          (p) => p.owner === user.username,
        );

        if (userProfile?.subscriptionId) {
          const result = await getClient().mutations.previewStripeSubscription({
            subscriptionId: userProfile.subscriptionId,
            newPriceId: "dummy",
            preview: false,
          });

          if (
            !("errors" in result && result.errors) &&
            result.data?.status
          ) {
            return {
              tier: "pro",
              status: result.data.status as
                | "active"
                | "canceled"
                | "past_due"
                | "trialing",
              currentPeriodEnd: result.data.currentPeriodEnd ?? undefined,
              planType: result.data.planType as
                | "monthly"
                | "yearly"
                | undefined,
              trialEnd: result.data.trialEnd ?? undefined,
            };
          }
        }
      } catch {
        // Fallback: Cognito says active/trialing, trust it
      }

      return {
        tier: "pro",
        status: subscriptionStatus as "active" | "trialing",
      };
    }

    // For other statuses (canceled, past_due), still return them
    return {
      tier: subscriptionStatus === "canceled" ? "free" : "pro",
      status: subscriptionStatus as
        | "active"
        | "canceled"
        | "past_due"
        | "trialing"
        | "none",
    };
  } catch {
    return { tier: "free", status: "none" };
  }
}
